import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
  TabContent,
  Table,
  TabPane,
} from "reactstrap";
import classNames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";

type CarteraVencidaRegistro = {
  fuente: string;
  idCuenta: number | null;
  moneda: string | null;
  idPersona: number | null;
  persona: string | null;
  idMovimiento: number | null;
  nroMovimiento: string | null;
  concepto: string | null;
  condicion: string | null;
  fecha: string | null;
  vencimiento: string | null;
  diasAtraso: number | null;
  idSucursal: number | null;
  sucursal: string | null;
  tipoCliente: string | null;
  tipoVencimiento: string | null;
  mascara: string | null;
  saldoAcumulado: number | null;
  monto: number;
  haber: number;
  dias0: number;
  dias10: number;
  dias30: number;
  dias60: number;
  dias90: number;
  dias120: number;
  dias120Mas: number;
  total: number;
  saldoPendiente: number;
};

type CarteraVencidaResponse = {
  filtros: {
    fechaDesde: string;
    fechaHasta: string;
    tipoFecha: string;
    tipoSaldo: string;
    moneda: string;
    sucursalId: number | null;
    personaId: number | null;
  };
  fuentes: string[];
  resumen: {
    registros: number;
    monto: number;
    haber: number;
    dias0: number;
    dias10: number;
    dias30: number;
    dias60: number;
    dias90: number;
    dias120: number;
    dias120Mas: number;
    total: number;
    saldoPendiente: number;
  };
  registros: CarteraVencidaRegistro[];
};

type FiltrosCartera = {
  fechaDesde: string;
  fechaHasta: string;
  tipoFecha: string;
  tipoSaldo: string;
  moneda: string;
  fuente: string;
};

type MonedaOption = {
  idmoneda: string;
  descripcion: string;
};

type FuenteOption = {
  codigo: string;
  descripcion: string;
};

const TIPO_FECHA_OPTIONS = [
  { value: "0", label: "Emisión" },
  { value: "1", label: "Vencimiento" },
];

const TIPO_SALDO_OPTIONS = [
  { value: "0", label: "Solo con saldo" },
  { value: "1", label: "Todas las cuentas" },
];

const monedaCodigoMap: Record<string, string> = {
  GS: "PYG",
  PYG: "PYG",
  USD: "USD",
  RS: "BRL",
  BRL: "BRL",
};

const crearFiltrosPorDefecto = (): FiltrosCartera => {
  const hoy = new Date();
  const haceUnAnho = new Date();
  haceUnAnho.setFullYear(haceUnAnho.getFullYear() - 1);
  return {
    fechaDesde: haceUnAnho.toISOString().slice(0, 10),
    fechaHasta: hoy.toISOString().slice(0, 10),
    tipoFecha: "0",
    tipoSaldo: "0",
    moneda: "",
    fuente: "",
  };
};

const numeroFormatter = new Intl.NumberFormat("es-PY", {
  maximumFractionDigits: 0,
});

const formatearMoneda = (valor: number, moneda?: string | null) => {
  const codigo = monedaCodigoMap[moneda ?? "GS"] ?? "PYG";
  const decimales = codigo === "PYG" ? 0 : 2;
  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency: codigo,
    maximumFractionDigits: decimales,
    minimumFractionDigits: decimales,
  }).format(valor ?? 0);
};

const construirQuery = (filtros: FiltrosCartera) => {
  const params = new URLSearchParams();
  Object.entries(filtros).forEach(([clave, valor]) => {
    if (valor !== undefined && valor !== null) {
      const texto = String(valor).trim();
      if (texto.length > 0) {
        params.set(clave, texto);
      }
    }
  });
  return params.toString();
};

type AgrupadoResumen = {
  etiqueta: string;
  registros: number;
  monto: number;
  haber: number;
  saldo: number;
};

type DetalleTabKey = "detalle" | "proveedor" | "vencimiento" | "sucursal";
type AgrupacionTabKey = Exclude<DetalleTabKey, "detalle">;

const FINANZAS_TABS = [{ id: "detalle", label: "Detalle" }];

const InformesFinanzasCarteraVencidaPage = () => {
  const [filters, setFilters] = useState<FiltrosCartera>(() => crearFiltrosPorDefecto());
  const [appliedFilters, setAppliedFilters] = useState<FiltrosCartera>(filters);
  const [data, setData] = useState<CarteraVencidaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monedas, setMonedas] = useState<MonedaOption[]>([]);
  const [fuentes, setFuentes] = useState<FuenteOption[]>([]);
  const [loadingMonedas, setLoadingMonedas] = useState(false);
  const [loadingFuentes, setLoadingFuentes] = useState(false);
  const [fuentesError, setFuentesError] = useState<string | null>(null);
  const [combosError, setCombosError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [detalleTab, setDetalleTab] = useState<"detalle" | "proveedor" | "vencimiento" | "sucursal">("detalle");
  const [activeTab, setActiveTab] = useState("detalle");

  useEffect(() => {
    document.title = "Finanzas - Cartera vencida | Saas";
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = construirQuery(appliedFilters);
      const respuesta = await requestJson<CarteraVencidaResponse>(
        `/api/informes/finanzas/cartera-vencida?${query}`,
      );
      setData(respuesta);
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : "No pudimos cargar el informe.";
      setError(mensaje);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelado = false;
    const cargarFuentes = async () => {
      setLoadingFuentes(true);
      setFuentesError(null);
      try {
        const lista = await requestJson<FuenteOption[]>(
          "/api/informes/finanzas/cartera-vencida/fuentes",
          { cache: "no-store" },
        );
        if (cancelado) return;
        setFuentes(lista);
        setFilters((prev) => {
          if (prev.fuente || lista.length === 0) {
            return prev;
          }
          return {
            ...prev,
            fuente: lista[0].codigo,
          };
        });
      } catch (err) {
        if (cancelado) return;
        const mensaje =
          err instanceof Error
            ? err.message
            : "No pudimos obtener las fuentes disponibles.";
        setFuentesError(mensaje);
        setFuentes([]);
      } finally {
        if (!cancelado) {
          setLoadingFuentes(false);
        }
      }
    };

    cargarFuentes();

    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    const fuenteActual = filters.fuente.trim();
    let cancelado = false;
    setCombosError(null);

    if (!fuenteActual) {
      setMonedas([]);
      setFilters((prev) => {
        if (prev.moneda === "") {
          return prev;
        }
        return {
          ...prev,
          moneda: "",
        };
      });
      return () => {
        cancelado = true;
      };
    }

    const fetchMonedas = async () => {
      setLoadingMonedas(true);
      try {
        const lista = await requestJson<MonedaOption[]>(
          `/api/informes/finanzas/cartera-vencida/monedas?fuente=${encodeURIComponent(fuenteActual)}`,
          { cache: "no-store" },
        );
        if (cancelado) return;
        setMonedas(lista);
        setFilters((prev) => {
          if (prev.fuente.trim() !== fuenteActual) {
            return prev;
          }
          if (prev.moneda) {
            return prev;
          }
          const primera = lista[0] ? String(lista[0].idmoneda) : "";
          return {
            ...prev,
            moneda: primera,
          };
        });
      } catch (err) {
        if (cancelado) return;
        const mensaje =
          err instanceof Error
            ? err.message
            : "No pudimos cargar la lista de monedas.";
        setCombosError(mensaje);
        setMonedas([]);
      } finally {
        if (!cancelado) {
          setLoadingMonedas(false);
        }
      }
    };

    fetchMonedas();

    return () => {
      cancelado = true;
    };
  }, [filters.fuente]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "fuente") {
      setFilters((prev) => ({
        ...prev,
        fuente: value,
        moneda: "",
      }));
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    const defaults = crearFiltrosPorDefecto();
    setFilters(defaults);
    setAppliedFilters(defaults);
  };

  const resumenCards = useMemo(
    () => [
      {
        label: "Registros",
        value: numeroFormatter.format(data?.resumen.registros ?? 0),
        description: "Cantidad de filas devueltas",
      },
      {
        label: "Saldo pendiente",
        value: formatearMoneda(data?.resumen.saldoPendiente ?? 0, data?.filtros.moneda),
        description: "Monto total pendiente",
      },
      {
        label: "Monto original",
        value: formatearMoneda(data?.resumen.monto ?? 0, data?.filtros.moneda),
        description: "Suma de montos",
      },
      {
        label: "Aplicado (haber)",
        value: formatearMoneda(data?.resumen.haber ?? 0, data?.filtros.moneda),
        description: "Pagos registrados",
      },
    ],
    [data],
  );

  const toggleFilters = () => setFiltersOpen((prev) => !prev);

  const bucketStats = useMemo(
    () => [
      { label: "0 días", value: data?.resumen.dias0 ?? 0 },
      { label: "10 días", value: data?.resumen.dias10 ?? 0 },
      { label: "30 días", value: data?.resumen.dias30 ?? 0 },
      { label: "60 días", value: data?.resumen.dias60 ?? 0 },
      { label: "90 días", value: data?.resumen.dias90 ?? 0 },
      { label: "120 días", value: data?.resumen.dias120 ?? 0 },
      { label: ">120 días", value: data?.resumen.dias120Mas ?? 0 },
    ],
    [data],
  );

  const registrosFiltrados = useMemo(() => {
    const termino = searchTerm.trim().toLowerCase();
    if (!termino) {
      return data?.registros ?? [];
    }
    return (data?.registros ?? []).filter((registro) => {
      const persona = registro.persona?.toLowerCase() ?? "";
      const documento = registro.nroMovimiento?.toLowerCase() ?? "";
      const sucursal = registro.sucursal?.toLowerCase() ?? "";
      const concepto = registro.concepto?.toLowerCase() ?? "";
      return (
        persona.includes(termino) ||
        documento.includes(termino) ||
        sucursal.includes(termino) ||
        concepto.includes(termino)
      );
    });
  }, [data?.registros, searchTerm]);

  const agruparRegistros = (
    selector: (registro: CarteraVencidaRegistro) => string,
  ): AgrupadoResumen[] => {
    const mapa = new Map<string, AgrupadoResumen>();
    for (const registro of registrosFiltrados) {
      const etiqueta = selector(registro);
      const existente = mapa.get(etiqueta) ?? {
        etiqueta,
        registros: 0,
        monto: 0,
        haber: 0,
        saldo: 0,
      };
      existente.registros += 1;
      existente.monto += registro.monto ?? 0;
      existente.haber += registro.haber ?? 0;
      existente.saldo += registro.saldoPendiente ?? 0;
      mapa.set(etiqueta, existente);
    }
    return Array.from(mapa.values()).sort((a, b) => b.saldo - a.saldo);
  };

  const resumenPorProveedor = useMemo(
    () =>
      agruparRegistros(
        (registro) => registro.persona?.trim() || "Sin proveedor",
      ),
    [registrosFiltrados],
  );
  const resumenPorVencimiento = useMemo(
    () =>
      agruparRegistros(
        (registro) => registro.vencimiento?.trim() || "Sin fecha",
      ),
    [registrosFiltrados],
  );
  const resumenPorSucursal = useMemo(
    () =>
      agruparRegistros(
        (registro) => registro.sucursal?.trim() || "Sin sucursal",
      ),
    [registrosFiltrados],
  );

  const agrupacionesPorTab = useMemo(
    () =>
      ({
        proveedor: { label: "Proveedor", datos: resumenPorProveedor },
        vencimiento: { label: "Fecha de vencimiento", datos: resumenPorVencimiento },
        sucursal: { label: "Sucursal", datos: resumenPorSucursal },
      }) as Record<AgrupacionTabKey, { label: string; datos: AgrupadoResumen[] }>,
    [resumenPorProveedor, resumenPorVencimiento, resumenPorSucursal],
  );

  const renderTabContent = (tabId: string) => {
    switch (tabId) {
      case "detalle":
      default: {
        const mostrarResumenAgrupado = detalleTab !== "detalle";
        const resumenActual = mostrarResumenAgrupado
          ? agrupacionesPorTab[detalleTab as AgrupacionTabKey]
          : null;

        return (
          <>
            <Offcanvas isOpen={filtersOpen} toggle={toggleFilters} direction="end">
              <OffcanvasHeader toggle={toggleFilters}>Filtros</OffcanvasHeader>
              <OffcanvasBody>
                <Form onSubmit={handleSubmit}>
                  <FormGroup className="mb-3">
                    <Label for="fuente">
                      Fuente{" "}
                      {loadingFuentes && <Spinner size="sm" />}
                    </Label>
                    <Input
                      id="fuente"
                      name="fuente"
                      type="select"
                      value={filters.fuente}
                      onChange={handleChange}
                      disabled={loadingFuentes || fuentes.length === 0}
                    >
                      <option value="">
                        {loadingFuentes ? "Cargando..." : "Seleccioná una fuente"}
                      </option>
                      {fuentes.map((fuente) => (
                        <option key={fuente.codigo} value={fuente.codigo}>
                          {fuente.descripcion}
                        </option>
                      ))}
                    </Input>
                    {fuentesError ? (
                      <Alert color="warning" className="mt-2">
                        {fuentesError}
                      </Alert>
                    ) : (
                      <small className="text-muted">
                        El informe usa las bases configuradas en el backend.
                      </small>
                    )}
                  </FormGroup>
                  {combosError && <Alert color="warning">{combosError}</Alert>}
                  <FormGroup className="mb-3">
                    <Label for="fechaDesde">Desde</Label>
                    <Input
                      id="fechaDesde"
                      name="fechaDesde"
                      type="date"
                      value={filters.fechaDesde}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup className="mb-3">
                    <Label for="fechaHasta">Hasta</Label>
                    <Input
                      id="fechaHasta"
                      name="fechaHasta"
                      type="date"
                      value={filters.fechaHasta}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup className="mb-3">
                    <Label for="tipoFecha">Tipo de fecha</Label>
                    <Input
                      id="tipoFecha"
                      name="tipoFecha"
                      type="select"
                      value={filters.tipoFecha}
                      onChange={handleChange}
                    >
                      {TIPO_FECHA_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup className="mb-3">
                    <Label for="tipoSaldo">Tipo de saldo</Label>
                    <Input
                      id="tipoSaldo"
                      name="tipoSaldo"
                      type="select"
                      value={filters.tipoSaldo}
                      onChange={handleChange}
                    >
                      {TIPO_SALDO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      Seleccioná 1 para incluir también cuentas sin saldo pendiente.
                    </small>
                  </FormGroup>
                  <FormGroup className="mb-3">
                    <Label for="moneda">Moneda</Label>
                    <Input
                      id="moneda"
                      name="moneda"
                      type="select"
                      value={filters.moneda}
                      onChange={handleChange}
                      disabled={!filters.fuente.trim() || loadingMonedas || monedas.length === 0}
                    >
                      <option value="">{loadingMonedas ? "Cargando..." : "Seleccioná una moneda"}</option>
                      {monedas.map((moneda) => (
                        <option key={moneda.idmoneda} value={moneda.idmoneda}>
                          {moneda.descripcion}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">Lista obtenida de la base seleccionada.</small>
                  </FormGroup>
                  <FormGroup className="mb-4">
                    <Label>Sucursal</Label>
                    <Input type="text" value="Todas" disabled readOnly />
                  </FormGroup>
                  <div className="d-flex gap-2">
                    <Button type="submit" color="primary" className="w-100" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : "Aplicar"}
                    </Button>
                    <Button type="button" color="light" className="w-100" onClick={handleReset} disabled={loading}>
                      Limpiar
                    </Button>
                  </div>
                </Form>
              </OffcanvasBody>
            </Offcanvas>
            <Row>
              <Col>
                <Row className="g-3">
                  {resumenCards.map((card) => (
                    <Col key={card.label} md={6} xl={3}>
                      <Card>
                        <CardBody>
                          <p className="text-muted text-uppercase mb-2">{card.label}</p>
                          <h4 className="mb-1">{card.value}</h4>
                          <p className="text-muted mb-0">{card.description}</p>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Card className="mt-4">
                  <CardBody>
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                      <div>
                        <h4 className="card-title mb-1">Distribución por días de atraso</h4>
                        <p className="text-muted mb-0">
                          Datos filtrados del {appliedFilters.fechaDesde} al {appliedFilters.fechaHasta}
                        </p>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {data?.fuentes.map((fuente) => (
                          <Badge key={fuente} pill color="secondary" className="bg-secondary-subtle text-body">
                            {fuente}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Row className="g-3">
                      {bucketStats.map((bucket) => (
                        <Col sm={6} md={4} key={bucket.label}>
                          <div className="p-3 rounded border border-dashed">
                            <p className="text-muted text-uppercase small mb-1">{bucket.label}</p>
                            <h5 className="mb-0">{formatearMoneda(bucket.value, data?.filtros.moneda)}</h5>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>

                <Card className="mt-4">
                  <CardHeader className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
                    <div>
                      <h4 className="card-title mb-0">Detalle de cuentas</h4>
                      <p className="text-muted mb-0">
                        Mostrando {numeroFormatter.format(registrosFiltrados.length)} de{" "}
                        {numeroFormatter.format(data?.resumen.registros ?? 0)} registros
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-wrap w-100 w-md-auto">
                      <div className="btn-group" role="group" aria-label="Ver detalle">
                        <Button
                          color={detalleTab === "detalle" ? "primary" : "light"}
                          onClick={() => setDetalleTab("detalle")}
                        >
                          Detalle
                        </Button>
                        <Button
                          color={detalleTab === "proveedor" ? "primary" : "light"}
                          onClick={() => setDetalleTab("proveedor")}
                        >
                          Por proveedor
                        </Button>
                        <Button
                          color={detalleTab === "vencimiento" ? "primary" : "light"}
                          onClick={() => setDetalleTab("vencimiento")}
                        >
                          Por vencimiento
                        </Button>
                        <Button
                          color={detalleTab === "sucursal" ? "primary" : "light"}
                          onClick={() => setDetalleTab("sucursal")}
                        >
                          Por sucursal
                        </Button>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap flex-md-nowrap w-100">
                        <InputGroup style={{ minWidth: 320 }} className="flex-grow-1">
                          <InputGroupText className="bg-body-secondary border-0">
                            <i className="ri-search-line" />
                          </InputGroupText>
                          <Input
                            type="search"
                            placeholder="Buscar cliente, documento, sucursal o concepto..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                          />
                        </InputGroup>
                        <Button
                          color="primary"
                          className="text-nowrap d-flex align-items-center gap-1"
                          disabled={registrosFiltrados.length === 0}
                          onClick={() => {
                            const filas = registrosFiltrados.map((registro) => ({
                              Fuente: registro.fuente ?? "",
                              Moneda: registro.moneda ?? "",
                              "ID Cuenta": registro.idCuenta ?? "",
                              Cliente: registro.persona ?? "",
                              "ID Cliente": registro.idPersona ?? "",
                              "Nro. Documento": registro.nroMovimiento ?? "",
                              Concepto: registro.concepto ?? "",
                              Condición: registro.condicion ?? "",
                              Fecha: registro.fecha ?? "",
                              Vencimiento: registro.vencimiento ?? "",
                              "Días Atraso": registro.diasAtraso ?? 0,
                              Sucursal: registro.sucursal ?? "",
                              "ID Sucursal": registro.idSucursal ?? "",
                              Monto: registro.monto ?? 0,
                              Haber: registro.haber ?? 0,
                              "Saldo Pendiente": registro.saldoPendiente ?? 0,
                              "Total Días 0": registro.dias0 ?? 0,
                              "Total Días 10": registro.dias10 ?? 0,
                              "Total Días 30": registro.dias30 ?? 0,
                              "Total Días 60": registro.dias60 ?? 0,
                              "Total Días 90": registro.dias90 ?? 0,
                              "Total Días 120": registro.dias120 ?? 0,
                              "Total +120": registro.dias120Mas ?? 0,
                            }));

                            const libro = XLSX.utils.book_new();
                            const hoja = XLSX.utils.json_to_sheet(filas);
                            XLSX.utils.book_append_sheet(libro, hoja, "Cartera vencida");
                            const fecha = new Date().toISOString().slice(0, 10);
                            XLSX.writeFile(libro, `cartera-vencida_${fecha}.xlsx`);
                          }}
                        >
                          <i className="ri-download-2-line" />
                          Descargar Excel
                        </Button>
                        <Button
                          color="secondary"
                          className="text-nowrap d-flex align-items-center gap-1"
                          onClick={toggleFilters}
                        >
                          <i className="ri-filter-3-line" />
                          Filtros
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="p-0">
                    {error && <Alert color="danger" className="m-3">{error}</Alert>}
                    {!error && detalleTab === "detalle" && (
                      <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Cliente</th>
                              <th>N° documento</th>
                              <th>Sucursal</th>
                              <th>Fecha / Vencimiento</th>
                              <th>Días atraso</th>
                              <th>Monto</th>
                              <th>Haber</th>
                              <th>Saldo</th>
                              <th>Fuente</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading && (
                              <tr>
                                <td colSpan={9} className="text-center py-4">
                                  <Spinner color="primary" />
                                </td>
                              </tr>
                            )}
                            {!loading && registrosFiltrados.length === 0 && (
                              <tr>
                                <td colSpan={9} className="text-center py-4 text-muted">
                                  No se encontraron datos para los filtros seleccionados.
                                </td>
                              </tr>
                            )}
                            {!loading &&
                              registrosFiltrados.map((registro, index) => {
                                const key =
                                  registro.idMovimiento?.toString() ??
                                  registro.nroMovimiento ??
                                  `${registro.idCuenta ?? "registro"}-${index}`;
                                const monedaMostrar = registro.moneda ?? data?.filtros.moneda;
                                return (
                                  <tr key={key}>
                                    <td>
                                      <div className="fw-medium">{registro.persona ?? "Sin cliente"}</div>
                                      <div className="text-muted small">
                                        {registro.idPersona ? `ID ${registro.idPersona}` : "Sin identificación"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fw-medium">{registro.nroMovimiento ?? "—"}</div>
                                      <div className="text-muted small">
                                        Cuenta {registro.idCuenta ?? "—"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fw-medium">{registro.sucursal ?? "Sin sucursal"}</div>
                                      <div className="text-muted small">
                                        {registro.idSucursal ? `ID ${registro.idSucursal}` : "—"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fw-medium">{registro.fecha ?? "Sin fecha"}</div>
                                      <div className="text-muted small">
                                        {registro.vencimiento ? `Vence ${registro.vencimiento}` : "Sin vencimiento"}
                                      </div>
                                    </td>
                                    <td>{numeroFormatter.format(registro.diasAtraso ?? 0)}</td>
                                    <td className="text-end">
                                      {formatearMoneda(registro.monto ?? 0, monedaMostrar)}
                                    </td>
                                    <td className="text-end">
                                      {formatearMoneda(registro.haber ?? 0, monedaMostrar)}
                                    </td>
                                    <td className="text-end">
                                      {formatearMoneda(registro.saldoPendiente ?? 0, monedaMostrar)}
                                    </td>
                                    <td>
                                      <Badge
                                        pill
                                        className="bg-info-subtle text-body fw-medium px-3 py-2 border border-info-subtle"
                                      >
                                        {registro.fuente ?? "Sin fuente"}
                                      </Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    {!error && mostrarResumenAgrupado && resumenActual && (
                      <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>{resumenActual.label}</th>
                              <th className="text-center">Registros</th>
                              <th className="text-end">Monto</th>
                              <th className="text-end">Haber</th>
                              <th className="text-end">Saldo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading && (
                              <tr>
                                <td colSpan={5} className="text-center py-4">
                                  <Spinner color="primary" />
                                </td>
                              </tr>
                            )}
                            {!loading && resumenActual.datos.length === 0 && (
                              <tr>
                                <td colSpan={5} className="text-center py-4 text-muted">
                                  No se encontraron datos para los filtros seleccionados.
                                </td>
                              </tr>
                            )}
                            {!loading &&
                              resumenActual.datos.map((item) => (
                                <tr key={`${resumenActual.label}-${item.etiqueta || "sin-valor"}`}>
                                  <td className="fw-medium">{item.etiqueta}</td>
                                  <td className="text-center">
                                    {numeroFormatter.format(item.registros)}
                                  </td>
                                  <td className="text-end">
                                    {formatearMoneda(item.monto, data?.filtros.moneda)}
                                  </td>
                                  <td className="text-end">
                                    {formatearMoneda(item.haber, data?.filtros.moneda)}
                                  </td>
                                  <td className="text-end">
                                    {formatearMoneda(item.saldo, data?.filtros.moneda)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        );
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title="Finanzas - Cartera vencida" pageTitle="Informes" />
        <Card className="border-0 shadow-sm">
          <CardBody className="pb-2">
            <Nav pills className="arrow-navtabs nav-success mb-3">
              {FINANZAS_TABS.map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    href="#"
                    className={classNames({ active: activeTab === tab.id })}
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab(tab.id);
                    }}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={activeTab} className="pt-2">
              {FINANZAS_TABS.map((tab) => (
                <TabPane tabId={tab.id} key={tab.id}>
                  {renderTabContent(tab.id)}
                </TabPane>
              ))}
            </TabContent>
          </CardBody>
        </Card>
      </Container>
      </div>
    
  );
};

export default InformesFinanzasCarteraVencidaPage;
